import React, { useState, useEffect } from 'react';
import { Col, Spinner, Alert } from 'react-bootstrap';
import { ethers } from 'ethers';
import Web3Modal from 'web3modal';
import LazyLoad from 'react-lazyload';
import { getBaseProvider } from '../utils/providers';

// Support multiple NFT contracts - add more contracts as needed
const TARGET_NFT_CONTRACTS = [
  '0x661cebd6550ca999870e7c467759c2b8da52852a',
  // Add more contract addresses here as needed
];
const ERC721_ABI = [
  'function balanceOf(address owner) external view returns (uint256)',
  'function tokenOfOwnerByIndex(address owner, uint256 index) external view returns (uint256)',
  'function tokenURI(uint256 tokenId) external view returns (string)',
  'function name() external view returns (string)',
  'function symbol() external view returns (string)',
  'function ownerOf(uint256 tokenId) external view returns (address)',
  // Add supportsInterface to check for ERC721 Enumerable
  'function supportsInterface(bytes4 interfaceId) external view returns (bool)'
];

// ERC721 Enumerable interface ID
const ERC721_ENUMERABLE_INTERFACE_ID = '0x780e9d63';

const parseTokenURI = (tokenURI) => {
  try {
    if (tokenURI.startsWith('data:application/json;base64,')) {
      const base64Data = tokenURI.replace('data:application/json;base64,', '');
      const jsonString = atob(base64Data);
      const metadata = JSON.parse(jsonString);
      return {
        name: metadata.name || '',
        description: metadata.description || '',
        image: metadata.image || '',
        animationUrl: metadata.animation_url || metadata.animationUrl || ''
      };
    } else if (tokenURI.startsWith('http')) {
      return {
        name: '',
        description: '',
        image: tokenURI,
        animationUrl: ''
      };
    }
    return {
      name: '',
      description: '',
      image: '',
      animationUrl: ''
    };
  } catch (e) {
    console.error('Error parsing tokenURI:', e);
    return {
      name: '',
      description: '',
      image: '',
      animationUrl: ''
    };
  }
};

const ScanForNFTs = ({ onNFTFound }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [error, setError] = useState(null);
  const [hasScanned, setHasScanned] = useState(false);
  const [foundCount, setFoundCount] = useState(0);
  const [scanWeb3Modal, setScanWeb3Modal] = useState(null);
  const [scanProvider, setScanProvider] = useState(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up the scan connection when component unmounts
      if (scanWeb3Modal) {
        try {
          scanWeb3Modal.clearCachedProvider();
        } catch (e) {
          console.log('Error clearing scan modal cache:', e);
        }
      }
      if (scanProvider && scanProvider.provider && scanProvider.provider.disconnect) {
        try {
          scanProvider.provider.disconnect();
        } catch (e) {
          console.log('Error disconnecting scan provider:', e);
        }
      }
    };
  }, [scanWeb3Modal, scanProvider]);

  const connectAndScan = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      // Create a separate Web3Modal instance for scanning
      // This won't interfere with the main app's wallet connection
      const web3Modal = new Web3Modal({
        network: 'any',
        cacheProvider: false, // Don't cache this connection
        providerOptions: {},
        disableInjectedProvider: false,
      });
      
      setScanWeb3Modal(web3Modal);
      
      // Connect wallet specifically for scanning
      const instance = await web3Modal.connect();
      const provider = new ethers.providers.Web3Provider(instance, 'any');
      setScanProvider(provider); // Store for cleanup
      const signer = provider.getSigner();
      
      // Get the current network
      const network = await provider.getNetwork();
      console.log('Scan wallet network:', network.chainId);
      
      // Get the wallet address
      const address = await signer.getAddress();
      setWalletAddress(address);
      setIsConnecting(false);
      
      // Always scan on Base Mainnet regardless of current network
      await scanForNFTs(address);
    } catch (err) {
      console.error('Failed to connect scan wallet:', err);
      setError('Failed to connect wallet for scanning. Please try again.');
      setIsConnecting(false);
    }
  };

  const scanSingleContract = async (contractAddress, address, provider) => {
    const contract = new ethers.Contract(contractAddress, ERC721_ABI, provider);
    
    // Check if contract supports ERC721 Enumerable for efficient scanning
    let supportsEnumerable = true;
    try {
      supportsEnumerable = await contract.supportsInterface(ERC721_ENUMERABLE_INTERFACE_ID);
      console.log(`Contract ${contractAddress} supports ERC721 Enumerable:`, supportsEnumerable);
    } catch (e) {
      console.log(`Could not check ERC721 Enumerable support for ${contractAddress}, assuming it is supported`);
    }
    
    if (!supportsEnumerable) {
      console.warn(`Contract ${contractAddress} does not support ERC721 Enumerable, skipping`);
      return [];
    }
    
    try {
      const balance = await contract.balanceOf(address);
      const balanceNum = balance.toNumber();
      
      if (balanceNum === 0) {
        return [];
      }
      
      const contractName = await contract.name();
      const contractSymbol = await contract.symbol();
      
      // Batch metadata fetching for better performance
      const tokenPromises = [];
      
      for (let i = 0; i < balanceNum; i++) {
        tokenPromises.push(
          (async () => {
            try {
              const tokenId = await contract.tokenOfOwnerByIndex(address, i);
              let tokenURI = '';
              let metadata = { name: '', description: '', image: '', animationUrl: '' };
              
              try {
                tokenURI = await contract.tokenURI(tokenId);
                metadata = parseTokenURI(tokenURI);
              } catch (e) {
                console.log('Could not fetch tokenURI for token', tokenId.toString());
              }
              
              return {
                tokenId: tokenId.toString(),
                contractAddress: contractAddress,
                name: metadata.name || `${contractName} #${tokenId}`,
                description: metadata.description,
                symbol: contractSymbol,
                tokenURI: tokenURI,
                image: metadata.image,
                animationUrl: metadata.animationUrl,
                owner: address
              };
            } catch (e) {
              console.error('Error fetching token at index', i, e);
              return null;
            }
          })()
        );
      }
      
      // Wait for all token fetches to complete
      const results = await Promise.all(tokenPromises);
      return results.filter(nft => nft !== null);
    } catch (e) {
      console.error(`Error scanning contract ${contractAddress}:`, e);
      return [];
    }
  };

  const scanForNFTs = async (address) => {
    setIsScanning(true);
    setError(null);

    try {
      const provider = getBaseProvider();
      const allNFTs = [];
      
      // Scan all configured contracts in parallel for better performance
      const contractPromises = TARGET_NFT_CONTRACTS.map(contractAddress => 
        scanSingleContract(contractAddress, address, provider)
      );
      
      const results = await Promise.all(contractPromises);
      
      // Flatten the results from all contracts
      for (const contractNFTs of results) {
        allNFTs.push(...contractNFTs);
      }
      
      setFoundCount(allNFTs.length);
      
      if (allNFTs.length > 0) {
        console.log(`Found ${allNFTs.length} NFTs across ${TARGET_NFT_CONTRACTS.length} contracts using ERC721 Enumerable.`);
      }
      
      if (onNFTFound) {
        onNFTFound(allNFTs);
      }
      
      setHasScanned(true);
    } catch (err) {
      console.error('Failed to scan for NFTs:', err);
      setError('Failed to scan for NFTs. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  // Create a placeholder image with the scan UI
  const renderScanContent = () => {
    if (isConnecting) {
      return (
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" variant="success" />
          <p style={{ marginTop: '10px', color: '#39ff14' }}>Connecting...</p>
        </div>
      );
    } else if (isScanning) {
      return (
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" variant="success" />
          <p style={{ marginTop: '10px', color: '#39ff14' }}>Scanning...</p>
        </div>
      );
    } else if (walletAddress && !hasScanned) {
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
          <p style={{ color: '#39ff14', fontSize: '14px' }}>Scanning...</p>
        </div>
      );
    } else if (hasScanned && foundCount === 0) {
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
          <p style={{ color: '#39ff14', fontSize: '14px' }}>No NFTs Found</p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setHasScanned(false);
              scanForNFTs(walletAddress);
            }}
            style={{
              marginTop: '5px',
              padding: '3px 8px',
              backgroundColor: '#39ff14',
              color: '#000',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Scan Again
          </button>
        </div>
      );
    } else if (hasScanned && foundCount > 0) {
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
          <p style={{ color: '#39ff14', fontSize: '14px', fontWeight: 'bold' }}>
            {foundCount} Found!
          </p>
          <div style={{ marginTop: '5px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                disconnectScanWallet();
              }}
              style={{
                padding: '3px 8px',
                backgroundColor: 'transparent',
                color: '#39ff14',
                border: '1px solid #39ff14',
                borderRadius: '3px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Disconnect
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔗</div>
          <p style={{ color: '#39ff14', fontSize: '14px' }}>
            Click to scan
          </p>
        </div>
      );
    }
  };

  // Function to disconnect the scan wallet
  const disconnectScanWallet = async () => {
    if (scanWeb3Modal) {
      try {
        scanWeb3Modal.clearCachedProvider();
      } catch (e) {
        console.log('Error clearing scan modal cache:', e);
      }
    }
    if (scanProvider && scanProvider.provider && scanProvider.provider.disconnect) {
      try {
        await scanProvider.provider.disconnect();
      } catch (e) {
        console.log('Error disconnecting scan provider:', e);
      }
    }
    
    // Reset state
    setWalletAddress(null);
    setHasScanned(false);
    setFoundCount(0);
    setScanWeb3Modal(null);
    setScanProvider(null);
    
    // Clear the found NFTs from parent
    if (onNFTFound) {
      onNFTFound([]);
    }
  };

  return (
    <>
      <Col xs={6} sm={6} md={4} lg={3} className="mb-4">
        <div
          className="nft-card scan-nft-card"
          style={{ 
            backgroundColor: '#222', 
            color: '#fff', 
            cursor: 'pointer',
            border: '2px dashed #39ff14'
          }}
          onClick={!walletAddress && !isConnecting && !isScanning ? connectAndScan : undefined}
          onMouseEnter={(e) => {
            if (!walletAddress && !isConnecting && !isScanning) {
              e.currentTarget.style.transform = 'scale(1.02)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <div style={{ position: 'relative' }}>
            <LazyLoad height={200} offset={0} once>
              <div 
                style={{ 
                  width: '100%',
                  aspectRatio: '1/1',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#222',
                  border: '2px dashed #39ff14'
                }}
              >
                {renderScanContent()}
              </div>
            </LazyLoad>
            {!walletAddress && !isConnecting && !isScanning && (
              <div className="overlay" style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#39ff14',
                color: '#000',
                padding: '5px 10px',
                borderRadius: '5px',
                fontWeight: 'bold',
                fontSize: '12px'
              }}>
                SCAN
              </div>
            )}
          </div>
          <div style={{ padding: '10px' }}>
            <h5 style={{ color: '#39ff14' }}>Scan for 3rd Party NFTs</h5>
            {walletAddress && (
              <>
                <p style={{ fontSize: '10px', wordBreak: 'break-all', marginBottom: '5px' }}>
                  {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    disconnectScanWallet();
                  }}
                  style={{
                    padding: '2px 6px',
                    backgroundColor: 'transparent',
                    color: '#39ff14',
                    border: '1px solid #39ff14',
                    borderRadius: '3px',
                    fontSize: '10px',
                    cursor: 'pointer'
                  }}
                >
                  Change Wallet
                </button>
              </>
            )}
          </div>
        </div>
      </Col>
      {error && (
        <Col xs={12}>
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </Col>
      )}
    </>
  );
};

export default ScanForNFTs;